import { useState } from 'react';
import { Plus } from 'lucide-react';
import { DEFAULT_FORM } from './useOrders';

export default function CreateOrderModal({ open, wallets, onClose, onSubmit }) {
  const [orderForm, setOrderForm] = useState(DEFAULT_FORM);

  if (!open) return null;

  const totalDispersionPct = orderForm.payee_wallets.reduce(
    (sum, p) => sum + (parseFloat(p.percentage) || 0),
    0,
  );

  const reset = () => setOrderForm(DEFAULT_FORM);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await onSubmit(orderForm);
      reset();
      onClose();
      alert('Orden creada exitosamente');
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al crear orden');
    }
  };

  const updatePayee = (index, field, value) => {
    const next = [...orderForm.payee_wallets];
    next[index][field] = value;
    setOrderForm({ ...orderForm, payee_wallets: next });
  };

  const addPayee = () =>
    setOrderForm({
      ...orderForm,
      payee_wallets: [
        ...orderForm.payee_wallets,
        { _uid: `payee-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, wallet_id: '', percentage: '', description: '' },
      ],
    });

  const removePayee = (index) =>
    setOrderForm({
      ...orderForm,
      payee_wallets: orderForm.payee_wallets.filter((_, i) => i !== index),
    });

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto"
      data-testid="create-order-modal"
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full my-8">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold" style={{ color: '#051C2C' }}>Nueva Orden de Pago</h2>
          <p className="text-sm text-gray-600 mt-1">Crear orden con dispersión automática</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-4">
            <Field
              label="Monto *"
              type="number"
              step="0.01"
              required
              value={orderForm.amount}
              onChange={(v) => setOrderForm({ ...orderForm, amount: v })}
              testid="order-amount-input"
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Moneda</label>
              <select
                value={orderForm.currency}
                onChange={(e) => setOrderForm({ ...orderForm, currency: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="VES">VES (Bolívares)</option>
                <option value="USD">USD (Dólares)</option>
              </select>
            </div>
          </div>

          <Field
            label="Descripción *"
            type="text"
            required
            value={orderForm.description}
            onChange={(v) => setOrderForm({ ...orderForm, description: v })}
            testid="order-description-input"
          />

          <div className="grid grid-cols-2 gap-4">
            <Field
              label="Referencia *"
              type="text"
              required
              placeholder="ORD-001"
              value={orderForm.order_reference}
              onChange={(v) => setOrderForm({ ...orderForm, order_reference: v })}
              testid="order-reference-input"
            />
            <Field
              label="Expira en (min)"
              type="number"
              value={orderForm.expires_in_minutes}
              onChange={(v) => setOrderForm({ ...orderForm, expires_in_minutes: v })}
            />
          </div>

          <div className="border-t pt-4">
            <h3 className="font-semibold mb-3" style={{ color: '#003594' }}>Información del Pagador</h3>
            <div className="grid grid-cols-2 gap-4">
              <Field
                label="Nombre *"
                type="text"
                required
                value={orderForm.payer_name}
                onChange={(v) => setOrderForm({ ...orderForm, payer_name: v })}
              />
              <Field
                label="Email *"
                type="email"
                required
                value={orderForm.payer_email}
                onChange={(v) => setOrderForm({ ...orderForm, payer_email: v })}
              />
              <Field
                label="Documento *"
                type="text"
                required
                placeholder="V12345678"
                value={orderForm.payer_document}
                onChange={(v) => setOrderForm({ ...orderForm, payer_document: v })}
              />
              <Field
                label="Teléfono *"
                type="text"
                required
                placeholder="04241234567"
                value={orderForm.payer_phone}
                onChange={(v) => setOrderForm({ ...orderForm, payer_phone: v })}
              />
            </div>
          </div>

          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-semibold" style={{ color: '#003594' }}>Dispersión Automática</h3>
                <p className="text-xs text-gray-600">
                  Total: {totalDispersionPct.toFixed(1)}%
                  {totalDispersionPct > 100 && <span className="text-red-600 ml-2">¡Excede 100%!</span>}
                </p>
              </div>
              <button
                type="button"
                onClick={addPayee}
                disabled={wallets.length === 0}
                className="text-sm px-3 py-1 rounded-lg disabled:opacity-50"
                style={{ backgroundColor: '#E8F3FA', color: '#003594' }}
              >
                <Plus className="w-4 h-4 inline mr-1" />
                Agregar Beneficiario
              </button>
            </div>

            {wallets.length === 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                <p className="text-sm text-yellow-800">
                  Crea wallets primero para poder configurar la dispersión
                </p>
              </div>
            )}

            {orderForm.payee_wallets.length > 0 && (
              <div className="space-y-2">
                {orderForm.payee_wallets.map((payee, index) => (
                  <div key={index} className="flex gap-2 items-start p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1 space-y-2">
                      <select
                        value={payee.wallet_id}
                        onChange={(e) => updatePayee(index, 'wallet_id', e.target.value)}
                        className="w-full px-2 py-1 text-sm border rounded"
                      >
                        <option value="">Seleccionar wallet...</option>
                        {wallets.map((w) => (
                          <option key={w.wallet_id} value={w.wallet_id}>
                            {w.wallet_name} ({w.wallet_type})
                          </option>
                        ))}
                      </select>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="number"
                          step="0.1"
                          placeholder="% Porcentaje"
                          value={payee.percentage}
                          onChange={(e) => updatePayee(index, 'percentage', e.target.value)}
                          className="w-full px-2 py-1 text-sm border rounded"
                        />
                        <input
                          type="text"
                          placeholder="Descripción"
                          value={payee.description}
                          onChange={(e) => updatePayee(index, 'description', e.target.value)}
                          className="w-full px-2 py-1 text-sm border rounded"
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removePayee(index)}
                      className="text-red-600 hover:text-red-800 text-xl"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              data-testid="cancel-order-btn"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={totalDispersionPct > 100}
              className="flex-1 px-4 py-2 text-white rounded-lg disabled:opacity-50"
              style={{ backgroundColor: '#003594' }}
              data-testid="submit-order-btn"
            >
              Crear Orden
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, type, value, onChange, placeholder, required, step, testid }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type={type}
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        data-testid={testid}
        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
      />
    </div>
  );
}
